/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ErrorResponseModel } from '../../../../../../core/models';
import { GslbApiService } from '../../../http';
import { GslbBackendModel, GslbFrontendModel, GslbRealServiceModel, GslbTopologyModel } from '../../../models';
import { IPModel } from '../../../models/ip.model';
import { Injectable } from '@angular/core';
import { LogService, UtilsService } from '../../../../../../core/services';
import { WizardControllerStateService } from './wizardControllerState.service';
import { WizardService } from './wizard.service';
import { WizardTitleEnum } from '../../../enums';
import { concatMap } from 'rxjs/operators';
import { from, Observable, pipe } from 'rxjs';

@Injectable()
export class GslbSubmissionService {
    private apiService: any;

    constructor(
        private gslbApiService: GslbApiService,
        private logService: LogService,
        private utilsService: UtilsService,
        private wizardControllerStateService: WizardControllerStateService,
        private wizardService: WizardService
    ) {
    }

    /**
     * Set the api service.
     */
    public setApiService(): void {
        this.apiService = this.gslbApiService;
    }

    /**
     * Concat helper
     * @param controllerState
     */
    public concatMapHelper(controllerState: any): any {
        return pipe(
            concatMap(() => this.submitService(controllerState, WizardTitleEnum.GLOBALNAMES)),
            concatMap(() => this.submitService(controllerState, WizardTitleEnum.MEMBERS)),
            concatMap(() => this.submitService(controllerState, WizardTitleEnum.POOLS)),
            concatMap(() => this.submitService(controllerState, WizardTitleEnum.TOPOLOGIES)),
        );
    }

    /**
     * Submits service from controllerState based on service name.
     * @param controllerState
     * @param serviceName
     * @private
     */
    private submitService(controllerState: any, serviceName: string): Observable<any> {
        return new Observable(observer => {
            if (!controllerState[serviceName] || !controllerState[serviceName].length) {
                observer.next(false);
                observer.complete();

                return;
            }

            let counter = 1;
            let tempId: string = '';
            const numberOfServices = controllerState[serviceName].length;

            from(controllerState[serviceName] || [])
                .pipe(
                    concatMap(serviceData => {
                        this.wizardService.wizardProgressMessage = `Submitting ${serviceName} ${counter} of ${numberOfServices}`;
                        const serviceValues = this.wizardControllerStateService.getValuesFromServiceData(controllerState, serviceData);

                        tempId = serviceValues.id;
                        this.updateControllerStates(serviceName, controllerState, serviceValues);

                        return this.utilsService.isServiceId(serviceValues.id)
                            ? this.updateServiceApiCall(serviceName, serviceValues)
                            : this.createServiceApiCall(serviceName, serviceValues);

                    })
                )
                .subscribe({
                    next: (data: any): void => {
                        if (data) {
                            // Add our successful ID's to our success state store.
                            this.updateSuccessState(controllerState, data, tempId);

                            if (counter === numberOfServices) {
                                // Complete when all API calls have been made.
                                observer.next(data);
                                observer.complete();
                            }

                            counter++;
                        }
                    },
                    error: (error: ErrorResponseModel): void => {
                        error.extras = {
                            storedErrorId: tempId,
                            storedErrorIndex: counter - 1
                        };

                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Take the submit response and store our created service ID's
     * @param controllerState
     * @param data
     * @param tempId
     * @private
     */
    private updateSuccessState(controllerState: any, data: any, tempId: string): any {
        const service = this.wizardControllerStateService;
        for (const i in data) {
            if (data.hasOwnProperty(i)) {
                switch (i) {
                    case 'globalName':
                        service.pushIdToSuccessArray(controllerState, WizardTitleEnum.GLOBALNAMES, data[i].id, tempId);
                        break;
                    case 'member':
                        service.pushIdToSuccessArray(controllerState, WizardTitleEnum.MEMBERS, data[i].id, tempId);
                        break;
                    case 'pool':
                        service.pushIdToSuccessArray(controllerState, WizardTitleEnum.POOLS, data[i].id, tempId);
                        break;
                    default:
                        service.pushIdToSuccessArray(controllerState, `${i}s`, data[i].id, tempId);
                        break;
                }

                return data[i];
            }
        }
        this.logService.debug('wizardControllerState:updateSuccessState()', 'Success state', controllerState[WizardTitleEnum.SUCCESS]);
    }

    /**
     * Update controller state for each service with success values.
     * @param serviceName
     * @param controllerState
     * @param serviceValues
     * @private
     */
    private updateControllerStates(
        serviceName: string,
        controllerState: any,
        serviceValues: any
    ): void {
        if (serviceName === WizardTitleEnum.POOLS) {
            this.updatePools(controllerState, serviceValues);
        }

        if (serviceName === WizardTitleEnum.TOPOLOGIES) {
            this.updateTopologies(controllerState, serviceValues);
        }
    }

    /**
     * Update pools with global names and member ids.
     * @param controllerState
     * @param serviceValues
     * @private
     */
    private updatePools(controllerState: any, serviceValues: any): void {
        const globalNamesIDs = serviceValues.globalNamesIDs;
        const membersIDs = serviceValues.membersIDs;

        this.updateGlobalNameIds(controllerState, serviceValues, globalNamesIDs);
        this.updateMemberIds(controllerState, serviceValues, membersIDs);
    }

    /**
     * Update topologies with member ids.
     * @param controllerState
     * @param serviceValues
     * @private
     */
    private updateTopologies(controllerState: any, serviceValues: any): void {
        const memberId = serviceValues.member;

        if (memberId && !this.utilsService.isServiceId(memberId)) {
            const successMembersIDs = controllerState[WizardTitleEnum.SUCCESS][WizardTitleEnum.MEMBERS];
            if (memberId.startsWith('temp-')) {
                const match = successMembersIDs.find((item: any) => item.tempId === memberId);
                if (match) {
                    serviceValues.member = match.id;
                } else {
                    this.logService.error('submitService',
                        'unable to match member Id with success members and tempId'
                    );
                }
            }
        }
    }

    /**
     * Update global name Ids.
     * @param controllerState
     * @param serviceValues
     * @param globalNamesIDs
     * @private
     */
    private updateGlobalNameIds(controllerState: any, serviceValues: any, globalNamesIDs: string[]): void {
        const successGlobalNames = controllerState[WizardTitleEnum.SUCCESS][WizardTitleEnum.GLOBALNAMES];
        for (let i = 0; i < globalNamesIDs.length; i++) {
            if (globalNamesIDs[i].startsWith('temp-') && this.wizardControllerStateService.tempIdsAreUnique(successGlobalNames)) {
                const match = successGlobalNames.find((item: any) => item.tempId === globalNamesIDs[i]);
                if (match) {
                    globalNamesIDs[i] = match.id;
                } else {
                    this.logService.error('submitService', 'unable to match globalname Id with success globalname and tempId');
                }
            }
        }

        serviceValues.globalNamesIDs = globalNamesIDs;
    }

    /**
     * Update member Ids.
     * @param controllerState
     * @param serviceValues
     * @param memberIDs
     * @private
     */
    private updateMemberIds(controllerState: any, serviceValues: any, memberIDs: string[]): void {
        const successMembersIDs = controllerState[WizardTitleEnum.SUCCESS][WizardTitleEnum.MEMBERS];
        for (let i = 0; i < memberIDs.length; i++) {
            if (memberIDs[i].startsWith('temp-') && this.wizardControllerStateService.tempIdsAreUnique(successMembersIDs)) {
                const match = successMembersIDs.find((item: any) => item.tempId === memberIDs[i]);
                if (match) {
                    memberIDs[i] = match.id;
                } else {
                    this.logService.error('submitService', 'unable to match member Id with success globalname and tempId');
                }
            }
        }

        serviceValues.membersIDs = memberIDs;
    }

    /** Creates an POST API call based on service name and state.
     * @param serviceName
     * @param serviceFormData
     * @private
     */
    private updateServiceApiCall(serviceName: string, serviceFormData: any): Observable<any> {
        switch (serviceName) {
            case WizardTitleEnum.GLOBALNAMES:
                const globalName: GslbFrontendModel = <GslbFrontendModel>serviceFormData;

                return this.apiService.editGlobalName(globalName);
            case WizardTitleEnum.POOLS:
                const pool: GslbBackendModel = <GslbBackendModel>serviceFormData;

                return this.apiService.editPool(pool);
            case WizardTitleEnum.MEMBERS:
                const member: GslbRealServiceModel = <GslbRealServiceModel>serviceFormData;

                return this.apiService.editMember(member);
            case WizardTitleEnum.TOPOLOGIES:
                const topology: GslbTopologyModel = <GslbTopologyModel>serviceFormData;

                return this.apiService.editTopology(topology);
        }
    }

    /** Creates an POST API call based on service name and state.
     * @param serviceName
     * @param serviceFormData
     * @private
     */
    private createServiceApiCall(serviceName: string, serviceFormData: any): Observable<any> {
        delete serviceFormData.id; // when creating a service we shouldn't be sending an ID, however it is needed for
                                   // reference so this is the only place it can be deleted.
        switch (serviceName) {
            case WizardTitleEnum.GLOBALNAMES:
                const globalName: GslbFrontendModel = <GslbFrontendModel>serviceFormData;

                return this.apiService.addGlobalName(globalName);
            case WizardTitleEnum.POOLS:
                const pool: GslbBackendModel = <GslbBackendModel>serviceFormData;

                return this.apiService.addPool(pool);
            case WizardTitleEnum.MEMBERS:
                const member: GslbRealServiceModel = <GslbRealServiceModel>serviceFormData;

                return this.apiService.addMember(member);
            case WizardTitleEnum.TOPOLOGIES:
                const topology = this.formatTopology(serviceFormData);

                return this.apiService.addTopology(topology);
        }
    }

    /**
     * Format the topology for submission.
     * @param serviceFormData
     * @private
     */
    private formatTopology(serviceFormData: any): any {
        const topology: GslbTopologyModel = <GslbTopologyModel>serviceFormData;
        topology.subNet = <IPModel>{};
        topology.subNet.ip = serviceFormData.ip;
        topology.subNet.mask = serviceFormData.mask;

        return topology;
    }
}
